import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Megaphone, Send, Users, ShieldCheck, Ticket, Music, Building2, UserCircle,
  Calendar, Palette, Eye, Loader2, ArrowLeft, AlertTriangle,
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import api from '../../lib/api';
import { EMAIL_TEMPLATES, renderEmailHtml, TEMPLATE_FIELDS, getEmailTemplate } from '../../lib/emailTemplates';
import { extractDominantColor } from '../../lib/colorExtractor';
import { BrandingPanel } from '../../components/email/BrandingPanel';
import { DynamicBlocksPanel } from '../../components/email/DynamicBlocksPanel';
import { SavedTemplatesBar } from '../../components/email/SavedTemplatesBar';
import { EmailPreviewFrame } from '../../components/email/EmailPreviewFrame';
import { TestEmailButton } from '../../components/email/TestEmailButton';

const AUDIENCE_OPTIONS = [
  { id: 'all_users',        label: 'Todos los usuarios',          icon: Users,       desc: 'Cuentas públicas registradas',          accent: 'text-blue-400',   bg: 'bg-blue-500/10' },
  { id: 'consented_users',  label: 'Con consentimiento marketing', icon: ShieldCheck, desc: 'Aceptaron explícitamente recibir promos', accent: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { id: 'all_buyers',       label: 'Compradores históricos',      icon: Ticket,      desc: 'Cualquiera que alguna vez compró ticket', accent: 'text-amber-400',  bg: 'bg-amber-500/10' },
  { id: 'event_buyers',     label: 'Compradores del evento',      icon: Ticket,      desc: 'Buyers del evento seleccionado',          accent: 'text-violet-400', bg: 'bg-violet-500/10' },
  { id: 'djs',              label: 'DJs registrados',             icon: Music,       desc: 'DJs aprobados/activos en la plataforma',  accent: 'text-pink-400',   bg: 'bg-pink-500/10' },
  { id: 'producers',        label: 'Productoras',                 icon: Building2,   desc: 'Cuentas de productoras',                  accent: 'text-orange-400', bg: 'bg-orange-500/10' },
];

export default function AdminCampaignComposePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [audience, setAudience] = useState('all_users');
  const [counts, setCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(true);

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(searchParams.get('event') || '');
  const [selectedEvent, setSelectedEvent] = useState(null);

  // From / sender
  const [fromName, setFromName] = useState('Bravo Producciones');

  // Email content
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [templateId, setTemplateId] = useState('luxuryEvent');
  const [content, setContent] = useState({});
  const [accentOverride, setAccentOverride] = useState(null);

  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // ---- Load audience counts & events list ---------------------------------
  useEffect(() => {
    const load = async () => {
      try {
        const params = eventId ? { event_id: eventId } : {};
        const [c, ev] = await Promise.all([
          api.get('/public-users/admin/campaign/audience-counts', { params }),
          events.length === 0 ? api.get('/events/?limit=100') : Promise.resolve({ data: events }),
        ]);
        setCounts(c.data || {});
        if (events.length === 0) setEvents(ev.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingCounts(false);
      }
    };
    load();
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- When event changes, auto-fill content + extract brand color --------
  useEffect(() => {
    if (!eventId) {
      setSelectedEvent(null);
      setAccentOverride(null);
      return;
    }
    const ev = events.find(e => e.id === eventId);
    if (!ev) return;
    setSelectedEvent(ev);
    setContent(prev => ({
      ...prev,
      title: ev.title,
      subtitle: ev.date ? new Date(ev.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'long' }) : '',
      cta_url: `https://bravoproductora.com/eventos/${ev.slug || ev.id}`,
      event_image: ev.image_url || ev.flyer_url,
    }));
    // Extract dominant color from flyer
    if (ev.image_url) {
      extractDominantColor(ev.image_url).then(color => {
        if (color) setAccentOverride(color);
      }).catch(() => {});
    }
  }, [eventId, events]);

  // ---- Init defaults when template changes --------------------------------
  useEffect(() => {
    const tpl = getEmailTemplate(templateId);
    if (!tpl) return;
    setContent(prev => {
      const next = { ...tpl.defaults };
      // preserve fields the user already filled
      Object.keys(prev).forEach(k => { if (prev[k]) next[k] = prev[k]; });
      return next;
    });
  }, [templateId]);

  const html = useMemo(() => {
    return renderEmailHtml(templateId, { ...content, accent_override: content.accent_override || accentOverride });
  }, [templateId, content, accentOverride]);

  const audienceCount = counts[audience] || 0;

  const handleSend = async () => {
    if (!subject.trim()) return toast.error('Asunto obligatorio');
    if (!html || html.length < 30) return toast.error('El email está vacío. Completá el contenido.');
    if (audience === 'event_buyers' && !eventId) return toast.error('Tenés que elegir un evento para este segmento');
    if (audienceCount === 0) return toast.error(`No hay destinatarios en el segmento "${audience}"`);
    if (!window.confirm(`Vas a enviar esta campaña a ${audienceCount.toLocaleString()} destinatario(s) del segmento "${AUDIENCE_OPTIONS.find(a => a.id === audience)?.label}". ¿Confirmás?`)) return;

    setSending(true);
    try {
      const res = await api.post('/public-users/admin/campaign/send', {
        audience,
        subject: subject.trim(),
        preview_text: previewText.trim() || null,
        body: html,
        event_id: eventId || null,
        from_name: fromName.trim() || 'Bravo Producciones',
      });
      const { sent, failed, total_recipients } = res.data;
      if (failed === 0) {
        toast.success(`✅ ${sent} email(s) enviado(s) (${total_recipients} destinatarios)`);
      } else {
        toast.warning(`${sent} enviados, ${failed} fallidos`);
      }
      navigate('/admin/marketing');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar la campaña');
    } finally {
      setSending(false);
    }
  };

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6"
          data-testid="admin-campaign-compose-page"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white">
                <ArrowLeft size={16} className="mr-1" /> Volver
              </Button>
              <div>
                <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3 text-white">
                  <Megaphone className="text-[#D4AF37]" size={26} />
                  Crear Campaña
                </h1>
                <p className="text-zinc-500 text-sm mt-1">Enviá un email masivo a los segmentos de la plataforma</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TestEmailButton apiPath="/public-users/admin/campaign/test-send" subject={subject} html={html} askEmail />
              <Button variant="outline" onClick={() => setShowPreview(!showPreview)} className="border-zinc-700 text-zinc-300" data-testid="admin-campaign-preview-toggle">
                <Eye size={14} className="mr-2" /> {showPreview ? 'Ocultar preview' : 'Vista previa'}
              </Button>
              <Button onClick={handleSend} disabled={sending || audienceCount === 0} className="bg-gradient-to-r from-[#D4AF37] to-[#F5D25A] hover:from-[#F5D25A] hover:to-[#D4AF37] text-black font-bold" data-testid="admin-campaign-send-btn">
                {sending ? <Loader2 className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}
                Enviar a {audienceCount.toLocaleString()} destinatario(s)
              </Button>
            </div>
          </div>

          {/* Legal warning */}
          {(audience === 'all_users' || audience === 'all_buyers') && (
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-300/90 leading-relaxed">
                Estás por enviar a usuarios que pueden no haber dado consentimiento explícito de marketing.
                Te recomendamos usar <strong>&quot;Con consentimiento marketing&quot;</strong> para campañas promocionales.
                Para comunicaciones transaccionales (cambios de servicio, info legal) este segmento es válido.
              </p>
            </div>
          )}

          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
            {/* LEFT: Audience + composer */}
            <div className="space-y-4">
              {/* Audience picker */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3" data-testid="audience-picker">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-white flex items-center gap-2"><Users size={16} className="text-[#D4AF37]" /> Audiencia</h3>
                  {loadingCounts ? <Loader2 size={14} className="animate-spin text-zinc-500" /> : (
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">{audienceCount.toLocaleString()} destinatarios</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {AUDIENCE_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const count = counts[opt.id];
                    const disabled = opt.id === 'event_buyers' && !eventId;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => !disabled && setAudience(opt.id)}
                        disabled={disabled}
                        data-testid={`audience-option-${opt.id}`}
                        className={`p-3 rounded-lg border text-left transition-all ${audience === opt.id ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-zinc-700 hover:border-zinc-600'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${opt.bg}`}>
                            <Icon size={14} className={opt.accent} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-xs font-bold text-white truncate">{opt.label}</p>
                              <span className={`text-xs font-mono font-bold ${opt.accent}`}>{count != null ? count.toLocaleString() : '—'}</span>
                            </div>
                            <p className="text-[10px] text-zinc-500 leading-tight mt-0.5">{opt.desc}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Event link */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3" data-testid="event-link-section">
                <h3 className="font-bold text-white flex items-center gap-2"><Calendar size={16} className="text-[#D4AF37]" /> Vincular a un evento (opcional)</h3>
                <select
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="event-link-select"
                >
                  <option value="">— Sin evento (campaña libre) —</option>
                  {events.map(ev => (
                    <option key={ev.id} value={ev.id}>{ev.title} {ev.date ? `· ${new Date(ev.date).toLocaleDateString('es-AR')}` : ''}</option>
                  ))}
                </select>
                {selectedEvent && (
                  <div className="flex items-center gap-3 p-2 rounded-md bg-zinc-800/50">
                    {selectedEvent.image_url && (
                      <img src={selectedEvent.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{selectedEvent.title}</p>
                      <p className="text-[10px] text-zinc-500">CTA apunta a /eventos/{selectedEvent.slug || selectedEvent.id}</p>
                    </div>
                    {accentOverride && (
                      <div className="flex items-center gap-1.5 text-[10px] text-zinc-400">
                        <Palette size={10} />
                        <span className="w-4 h-4 rounded border border-white/20" style={{ background: accentOverride }} />
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* From / Sender */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-2" data-testid="sender-section">
                <h3 className="font-bold text-white flex items-center gap-2"><UserCircle size={16} className="text-[#D4AF37]" /> Remitente</h3>
                <Label className="text-zinc-400 text-xs">Nombre que verán en la bandeja</Label>
                <Input
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="Bravo Producciones"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="from-name-input"
                />
                <p className="text-[10px] text-zinc-500">Email saldrá desde <span className="font-mono text-zinc-400">noreply@bravoproductora.com</span></p>
              </section>

              {/* Subject + preview text */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
                <h3 className="font-bold text-white">Asunto y avance</h3>
                <div>
                  <Label className="text-zinc-400 text-xs">Asunto</Label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Ej: ¡Última oportunidad! Catamarán Sunset Party"
                    maxLength={120}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="campaign-subject-input"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">{subject.length}/120</p>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Texto de avance (preheader)</Label>
                  <Input
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    placeholder="Línea que aparece después del asunto en la bandeja"
                    maxLength={150}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="campaign-preview-input"
                  />
                </div>
              </section>

              {/* Saved templates */}
              <SavedTemplatesBar
                apiPath="/public-users/admin/campaign/templates"
                templateId={templateId}
                content={content}
                onLoad={(t) => { setTemplateId(t.template_id); setContent(t.content || {}); }}
              />

              {/* Template picker */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
                <h3 className="font-bold text-white">Plantilla visual</h3>
                <div className="grid grid-cols-5 gap-2" data-testid="admin-template-picker">
                  {EMAIL_TEMPLATES.map(tpl => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => setTemplateId(tpl.id)}
                      data-testid={`admin-template-${tpl.id}`}
                      className={`p-2 rounded-lg border-2 transition-all ${templateId === tpl.id ? 'border-[#D4AF37]' : 'border-zinc-700 hover:border-zinc-600'}`}
                      title={tpl.description}
                    >
                      <div className="h-10 rounded-md mb-1" style={{ background: tpl.swatch }} />
                      <p className="text-[10px] text-zinc-300 truncate">{tpl.label}</p>
                    </button>
                  ))}
                </div>
              </section>

              {/* Content fields */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
                <h3 className="font-bold text-white">Contenido</h3>
                {TEMPLATE_FIELDS.map(field => (
                  <div key={field.key}>
                    <Label className="text-zinc-400 text-xs">{field.label}</Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        value={content[field.key] || ''}
                        onChange={(e) => setContent({ ...content, [field.key]: e.target.value })}
                        placeholder={field.placeholder}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        rows={3}
                        data-testid={`content-${field.key}`}
                      />
                    ) : (
                      <Input
                        value={content[field.key] || ''}
                        onChange={(e) => setContent({ ...content, [field.key]: e.target.value })}
                        placeholder={field.placeholder}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid={`content-${field.key}`}
                      />
                    )}
                  </div>
                ))}
              </section>

              {/* Branding */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
                <h3 className="font-bold text-white flex items-center gap-2"><Palette size={16} className="text-[#D4AF37]" /> Branding y estilo</h3>
                <BrandingPanel content={content} setContent={setContent} brandingApi="/public-users/admin/campaign/branding" />
              </section>

              {/* Dynamic blocks */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
                <DynamicBlocksPanel
                  content={content}
                  setContent={setContent}
                  events={events}
                  selectedEvent={selectedEvent}
                  eventUrlBase="https://bravoproductora.com/eventos"
                />
              </section>
            </div>

            {/* RIGHT: Preview */}
            <div className={`${showPreview ? 'block' : 'hidden lg:block'} sticky top-4 self-start`} data-testid="campaign-preview">
              <EmailPreviewFrame html={html} subject={subject} previewText={previewText} fromLabel={`${fromName} <noreply@bravoproductora.com>`} />
            </div>
          </div>
        </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
}
